import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import { getGoogleMapsNavUrl } from '@utils/googleMapsLink';
import DirectionsOutlinedIcon from '@mui/icons-material/DirectionsOutlined';
import { useTranslation } from '@hooks/useTranslation';

interface GoogleMapsLinkButtonProps {
  lat: number | null | undefined;
  lng: number | null | undefined;
  /** Visible label. Defaults to "Copy directions". */
  label?: string;
  /** Toast message after a successful copy. */
  successMessage?: string;
  /** Visual style — `chip` (rounded with bg) or `inline` (text-link style). */
  variant?: 'chip' | 'inline';
  /** Optional className override. */
  className?: string;
  /** Stop propagation by default — these buttons live inside cards/rows. */
  stopPropagation?: boolean;
}

/**
 * Copies a Google Maps navigation URL for the given coordinates to the
 * clipboard. Returns null when coordinates aren't available so the caller
 * doesn't render an empty link.
 */
export const GoogleMapsLinkButton: React.FC<GoogleMapsLinkButtonProps> = ({
  lat,
  lng,
  label = 'Copy directions',
  successMessage = 'Google Maps link copied',
  variant = 'chip',
  className,
  stopPropagation = true,
}) => {
  const tActionMessages = useTranslation('actionMessages');
  const hasCoords =
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lng);

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (stopPropagation) e.stopPropagation();
      if (!hasCoords) return;
      const url = getGoogleMapsNavUrl(lat as number, lng as number);
      try {
        await navigator.clipboard.writeText(url);
        toast.success(successMessage);
      } catch {
        // Fallback: select-and-copy via a hidden textarea for browsers that
        // block the async clipboard API outside of user gestures.
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand('copy');
          toast.success(successMessage);
        } catch {
          toast.error(tActionMessages.couldNotCopyLink);
        }
        document.body.removeChild(ta);
      }
    },
    [hasCoords, lat, lng, stopPropagation, successMessage, tActionMessages.couldNotCopyLink],
  );

  if (!hasCoords) return null;

  if (variant === 'inline') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={
          className ??
          'inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700'
        }
        title="Copy Google Maps directions link"
      >
        <DirectionsOutlinedIcon sx={{ fontSize: 14 }} />
        {label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={
        className ??
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-indigo-700 bg-white ring-1 ring-indigo-200 hover:bg-indigo-50'
      }
      title="Copy Google Maps directions link"
    >
      <DirectionsOutlinedIcon sx={{ fontSize: 14 }} />
      {label}
    </button>
  );
};

export default GoogleMapsLinkButton;
